import { SvgCheck } from '@common/icon';
import { cn } from '@core/utils';
import { useEffect, useRef, useState } from 'react';

import { useGetter } from '@/hooks/useGetter';
import { MODAL_CLOSING_STATE, useModalClosing } from '@/hooks/useModalClosing';
import type { HTMLTagProps } from '@/types';

export type ValidOptionValue = string | number;

export interface SelectChangeEvent<
  TOptionValue extends ValidOptionValue,
  TMultiple extends boolean,
  TCancelable extends boolean,
> {
  value: TMultiple extends true
    ? TOptionValue[]
    : TCancelable extends true
      ? TOptionValue | undefined
      : TOptionValue;
}

export type OptionProps<
  TOptionValue extends ValidOptionValue,
  TMultiple extends boolean = false,
  TCancelable extends boolean = false,
> = Omit<HTMLTagProps<'section'>, 'value' | 'onChange' | 'onKeyDown'> & {
  opened?: boolean;
  options?: {
    label: string;
    value: TOptionValue;
  }[];
  multiple?: TMultiple;
  cancelable?: TCancelable;
  value?: TMultiple extends true ? TOptionValue[] : TOptionValue | undefined;
  onChange?: (
    event: SelectChangeEvent<TOptionValue, TMultiple, TCancelable>,
  ) => void;
  onKeyDown?: (event: KeyboardEvent) => void;
  float?: 'top' | 'bottom';
};

const INITIAL_INDEX = -1;

export const Option = <
  TOptionValue extends ValidOptionValue = ValidOptionValue,
  TMultiple extends boolean = false,
  TCancelable extends boolean = false,
>({
  //* Select props
  opened = false,
  options,
  multiple = false as TMultiple,
  cancelable = false as TCancelable,
  value: selectedValue,
  onChange,
  onKeyDown,
  float = 'bottom',

  //* HTML section props
  className,
  ...restSectionProps
}: OptionProps<TOptionValue, TMultiple, TCancelable>) => {
  const closingTransition = useModalClosing({
    opened,
    closingDuration: 100,
  });

  const optionItemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const indexForSelectState = useState(INITIAL_INDEX);
  const [indexForSelect, setIndexForSelect] = indexForSelectState;

  const getIndexForSelect = useGetter(indexForSelect);

  useEffect(() => {
    if (closingTransition === MODAL_CLOSING_STATE.OPENED && options) {
      const prevFocusedElement = document.activeElement;

      const focusOptionItem = (indexForSelect: number) => {
        optionItemRefs.current[indexForSelect]?.focus();
        setIndexForSelect(indexForSelect);
      };

      const keyboardEvent = (event: KeyboardEvent) => {
        onKeyDown?.(event);

        let indexForSelect = getIndexForSelect();

        switch (event.key) {
          case 'ArrowUp':
            event.preventDefault();

            if (indexForSelect > 0) {
              indexForSelect -= 1;
              focusOptionItem(indexForSelect);
            }

            break;

          case 'ArrowDown':
            event.preventDefault();

            if (indexForSelect < options.length - 1) {
              indexForSelect += 1;
              focusOptionItem(indexForSelect);
            }

            break;

          case 'Enter':
            event.preventDefault();

            if (indexForSelect !== INITIAL_INDEX)
              optionItemRefs.current[indexForSelect]?.click();

            break;

          default: {
            if (prevFocusedElement instanceof HTMLElement)
              prevFocusedElement?.focus();
          }
        }
      };

      document.addEventListener('keydown', keyboardEvent);

      return () => document.removeEventListener('keydown', keyboardEvent);
    }
  }, [
    getIndexForSelect,
    onKeyDown,
    closingTransition,
    options,
    optionItemRefs,
    setIndexForSelect,
  ]);

  return closingTransition && options?.length ? (
    <section
      {...restSectionProps}
      className={cn(
        'animate-fade-in absolute z-[1] box-border w-full cursor-default rounded-xsmall border border-Gray-400 bg-Gray-white py-5 text-Gray-900 text-[inherit] animate-duration-[200ms]',
        float === 'top' && 'bottom-full mb-5',
        float === 'bottom' && 'top-full mt-5',
        closingTransition === MODAL_CLOSING_STATE.CLOSING &&
          'animate-fade-out animate-duration-[100ms]',
        className,
      )}
    >
      <ul className="m-0 flex max-h-[30dvh] snap-y snap-mandatory flex-col overflow-y-scroll p-0 hide-scrollbar">
        {options?.map(({ label, value }, index) => {
          const isHovered = index === indexForSelect;
          const isSelected = (() => {
            if (selectedValue === undefined) return false;

            if (multiple && Array.isArray(selectedValue)) {
              return (selectedValue as TOptionValue[]).includes(value);
            }

            return selectedValue === value;
          })();

          return (
            <li key={index} className="w-full snap-start">
              <button
                ref={(element) => {
                  optionItemRefs.current[index] = element;
                }}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={isSelected}
                className={cn(
                  'font-normal box-border flex min-w-full max-w-full cursor-pointer snap-start items-center gap-[1em] whitespace-pre-wrap border-none bg-transparent p-12 text-start font-[inherit] text-[inherit] text-inherit outline-none',
                  isHovered && 'bg-[#f5f6ff]',
                  isSelected && 'font-medium text-B-500',
                )}
                type="button"
                onClick={() => {
                  if (multiple) {
                    type MultipleSelectProps = OptionProps<
                      TOptionValue,
                      true,
                      TCancelable
                    >;
                    let valuesForSelect =
                      (selectedValue as TOptionValue[]) ?? [];

                    const handleChange =
                      onChange as MultipleSelectProps['onChange'];

                    if (cancelable) {
                      valuesForSelect = isSelected
                        ? valuesForSelect?.filter(
                            (selectedValue) => selectedValue !== value,
                          )
                        : [...valuesForSelect, value];
                    }

                    handleChange?.({
                      value: valuesForSelect,
                    });
                  } else {
                    type SingleSelectProps = OptionProps<
                      TOptionValue,
                      false,
                      TCancelable
                    >;
                    const handleChange =
                      onChange as SingleSelectProps['onChange'];

                    handleChange?.({
                      value: isSelected && cancelable ? undefined : value,
                    } as SelectChangeEvent<TOptionValue, false, TCancelable>);
                  }
                }}
                onMouseEnter={() => setIndexForSelect(index)}
              >
                <div>{label}</div>
                <div
                  className={cn(
                    'm-[0.2em] ml-auto',
                    isSelected ? 'visible' : 'invisible',
                  )}
                >
                  <SvgCheck className="size-[1.2em]" />
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  ) : (
    <></>
  );
};
